// Prepare an operator-supplied INTERIOR PDF for print, in the BROWSER.
//
// WHY THE BROWSER AND NOT THE WORKER
// The worker used to download the file and open it with pdf-lib. Measured on
// real files, pdf-lib peaks at roughly 3.3x the file size; Cloudflare gives a
// Worker 128 MB total. So ~30 MB was the true ceiling, and a 304 MB client
// photobook blew past it — the allocation threw outside the endpoint's
// try/catch and surfaced as a bare "Internal server error".
// The file is already on the operator's machine, so we do the work here where
// there is real memory, and the worker only records what happened.
//
// WHAT THIS DOES, IN ORDER
//   1. measure every page (honouring /Rotate, which pdf-lib's getSize ignores)
//   2. all pages must be the SAME size — mixed documents are refused
//   3. accept 200x297 (trim) or 206x303 (bleed) as-is; accept 210x297 (A4) and
//      convert it; refuse anything else
//   4. A4 -> 200x297 by narrowing the MediaBox/CropBox 5 mm each side. This is
//      a metadata change: no rasterising, no re-encoding, no file growth.
//      Measured on a 60-page A4 file: 0% growth, 15 ms.
//   5. add the Storiz intro/outro branding pages
//   6. pad with blanks to a multiple of 4 (the printer images 4 album pages per
//      A3 sheet, double-sided)
//
// WHY CROP AND NOT SCALE: A4 is already the correct HEIGHT (297 = 297); only the
// width is 10 mm over. Cropping keeps every remaining element at true size.
// Scaling to fit the width would shrink the whole page 4.8% and leave ~14 mm of
// white banding top and bottom, which reads as a printing fault on a photo book.
// Scaling the width alone would squash faces and text. The cost of cropping is
// that artwork within 5 mm of the left/right edge is lost, which is why the UI
// warns before it runs and why every conversion is logged per order.

import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import type { PDFImage, PDFPage } from 'pdf-lib';
import { LETTERBOX_FILL } from './photoFit';
import { assertPrintSafe } from './printPdfCheck';
import { drawOrderStamp, placeStamp } from './orderStamp';

export const PT_PER_MM = 72 / 25.4;
/** Finished interior page. Mirrors INTERIOR_TRIM_MM in worker/src/lib/interiorValidate.ts. */
export const INTERIOR_TRIM_MM = { wmm: 200, hmm: 297 } as const;
/** Standard A4 — accepted and converted. */
export const A4_MM = { wmm: 210, hmm: 297 } as const;
export const TOLERANCE_MM = 2;

export type InteriorFormat = 'trim' | 'bleed' | 'a4' | 'unknown';
export type ConversionMethod = 'none' | 'crop-a4';

export interface MeasuredPage {
  index: number;
  wmm: number;
  hmm: number;
  rotation: number;
}

/** Written to album_sessions.interior_manual_conversion — the audit trail. */
export interface InteriorConversionLog {
  at: string;
  filename: string;
  file_bytes: number;
  source_mm: { wmm: number; hmm: number };
  target_mm: { wmm: number; hmm: number };
  method: ConversionMethod;
  source_pages: number;
  branding_pages_added: number;
  /** Extra OUTRO pages appended to reach a multiple of 4. */
  outro_pages_added: number;
  /** Cream filler pages. Should be 0 — only used when the order has no outro. */
  blank_pages_added: number;
  final_pages: number;
  /** Bindery tag stamped on the intro page, e.g. "#1113 1/1". */
  order_label?: string | null;
}

export class InteriorPrepareError extends Error {
  constructor(message: string) { super(message); this.name = 'InteriorPrepareError'; }
}

const round2 = (n: number) => Math.round(n * 100) / 100;
const near = (a: number, b: number) => Math.abs(a - b) <= TOLERANCE_MM;

/** Bleed shape derived from the live setting, same as the worker does. */
export function bleedSizeMm(bleedMm: number): { wmm: number; hmm: number } {
  return {
    wmm: round2(INTERIOR_TRIM_MM.wmm + 2 * bleedMm),
    hmm: round2(INTERIOR_TRIM_MM.hmm + 2 * bleedMm),
  };
}

export function classifyFormat(wmm: number, hmm: number, bleedMm: number): InteriorFormat {
  const bleed = bleedSizeMm(bleedMm);
  if (near(wmm, INTERIOR_TRIM_MM.wmm) && near(hmm, INTERIOR_TRIM_MM.hmm)) return 'trim';
  if (near(wmm, bleed.wmm) && near(hmm, bleed.hmm)) return 'bleed';
  if (near(wmm, A4_MM.wmm) && near(hmm, A4_MM.hmm)) return 'a4';
  return 'unknown';
}

/** Total page count rounded UP to the next multiple of 4 (A3 double-sided imposition). */
export function padTo4(total: number): number {
  const r = total % 4;
  return r === 0 ? total : total + (4 - r);
}

/**
 * Rough peak-memory estimate for opening + re-saving a PDF of this size, so the
 * UI can refuse gracefully on a phone instead of crashing the tab. Derived from
 * measurement: parsing peaks near 3.3x, and re-serialising holds another copy.
 */
export function estimatePeakMemoryMb(fileBytes: number): number {
  return Math.round((fileBytes / 1048576) * 4.3);
}

/** Measure every page, applying /Rotate (pdf-lib's getSize does NOT). */
export function measurePages(doc: PDFDocument): MeasuredPage[] {
  const out: MeasuredPage[] = [];
  const count = doc.getPageCount();
  for (let i = 0; i < count; i++) {
    const page = doc.getPage(i);
    const media = page.getMediaBox();
    const rotation = ((page.getRotation().angle % 360) + 360) % 360;
    const swap = rotation === 90 || rotation === 270;
    const mw = media.width / PT_PER_MM;
    const mh = media.height / PT_PER_MM;
    out.push({
      index: i,
      wmm: round2(swap ? mh : mw),
      hmm: round2(swap ? mw : mh),
      rotation,
    });
  }
  return out;
}

/**
 * Decide what to do with a measured document. Pure — no PDF mutation — so the
 * UI can show the operator exactly what will happen BEFORE anything runs.
 */
export function planConversion(
  pages: MeasuredPage[],
  bleedMm: number,
): { method: ConversionMethod; format: InteriorFormat; source: { wmm: number; hmm: number } } {
  if (pages.length === 0) throw new InteriorPrepareError('Le PDF ne contient aucune page.');

  const distinct = [...new Set(pages.map((p) => `${p.wmm}x${p.hmm}`))];
  if (distinct.length > 1) {
    const sizes = distinct.map((s) => s.replace('x', ' × ') + ' mm').join(', ');
    throw new InteriorPrepareError(
      `Toutes les pages doivent avoir la même taille. Ce fichier en contient ${distinct.length} : ${sizes}. ` +
      `Ré-exportez l'intérieur avec une seule taille de page.`,
    );
  }

  const first = pages[0];
  const format = classifyFormat(first.wmm, first.hmm, bleedMm);
  const source = { wmm: first.wmm, hmm: first.hmm };

  if (format === 'trim' || format === 'bleed') return { method: 'none', format, source };
  if (format === 'a4') return { method: 'crop-a4', format, source };

  const bleed = bleedSizeMm(bleedMm);
  throw new InteriorPrepareError(
    `Format d'intérieur incorrect. Reçu : ${first.wmm} × ${first.hmm} mm. ` +
    `Attendu : ${INTERIOR_TRIM_MM.wmm} × ${INTERIOR_TRIM_MM.hmm} mm, ` +
    `${A4_MM.wmm} × ${A4_MM.hmm} mm (A4, converti automatiquement), ` +
    `ou ${bleed.wmm} × ${bleed.hmm} mm (avec ${bleedMm} mm de fond perdu). ` +
    `Ré-exportez l'intérieur à l'une de ces tailles.`,
  );
}

/** Narrow every page to the 200×297 window, centred. Metadata only. */
function cropToTrim(doc: PDFDocument): void {
  const targetW = INTERIOR_TRIM_MM.wmm * PT_PER_MM;
  const targetH = INTERIOR_TRIM_MM.hmm * PT_PER_MM;
  for (const page of doc.getPages()) {
    const mb = page.getMediaBox();
    const dx = (mb.width - targetW) / 2;
    const dy = (mb.height - targetH) / 2;
    page.setMediaBox(mb.x + dx, mb.y + dy, targetW, targetH);
    page.setCropBox(mb.x + dx, mb.y + dy, targetW, targetH);
  }
}

async function fetchBytes(url: string): Promise<{ bytes: Uint8Array; isPdf: boolean }> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const buf = new Uint8Array(await res.arrayBuffer());
  const isPdf = buf[0] === 0x25 && buf[1] === 0x50 && buf[2] === 0x44 && buf[3] === 0x46;
  return { bytes: buf, isPdf };
}

/**
 * Append a branding page (intro/outro) at `where`. Accepts a PDF or an image
 * URL, mirroring embedBookendPage() in pdfGenerator.ts. Non-fatal: a branding
 * page that fails to load must never block a print job, so we report it and
 * carry on rather than throwing.
 */
async function addBrandingPage(
  doc: PDFDocument,
  url: string | null,
  where: 'start' | 'end',
): Promise<PDFPage | null> {
  if (!url) return null;
  const targetW = INTERIOR_TRIM_MM.wmm * PT_PER_MM;
  const targetH = INTERIOR_TRIM_MM.hmm * PT_PER_MM;
  try {
    const { bytes, isPdf } = await fetchBytes(url);
    if (isPdf) {
      const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
      const [copied] = await doc.copyPages(src, [0]);
      copied.setMediaBox(0, 0, targetW, targetH);
      copied.setCropBox(0, 0, targetW, targetH);
      if (where === 'start') doc.insertPage(0, copied); else doc.addPage(copied);
      return copied;
    }
    let img: PDFImage;
    try { img = await doc.embedPng(bytes); }
    catch { img = await doc.embedJpg(bytes); }
    const page = where === 'start' ? doc.insertPage(0, [targetW, targetH]) : doc.addPage([targetW, targetH]);
    // Cover-fit so a bookend at any aspect ratio still fills the page.
    const scale = Math.max(targetW / img.width, targetH / img.height);
    const dw = img.width * scale, dh = img.height * scale;
    page.drawImage(img, { x: (targetW - dw) / 2, y: (targetH - dh) / 2, width: dw, height: dh });
    return page;
  } catch (e) {
    console.warn('[interiorPrepare] branding page skipped:', (e as Error)?.message ?? e);
    return null;
  }
}

export interface PrepareOptions {
  bleedMm: number;
  introPageUrl?: string | null;
  outroPageUrl?: string | null;
  /** Bindery marry-label, e.g. "#1113 1/1" — same string the cover carries. */
  orderLabel?: string | null;
  filename: string;
  onProgress?: (step: PrepareStep, detail?: string) => void;
}

export type PrepareStep =
  | 'reading' | 'measuring' | 'converting' | 'branding' | 'padding' | 'checking' | 'saving';

export interface PrepareResult {
  bytes: Uint8Array;
  log: InteriorConversionLog;
  /** True when nothing was altered — the operator's bytes are returned verbatim. */
  unchanged: boolean;
}

/**
 * Full pipeline. Throws InteriorPrepareError with a message meant to be shown
 * to the operator verbatim — never a bare technical error.
 */
export async function prepareInteriorPdf(
  fileBytes: Uint8Array,
  opts: PrepareOptions,
): Promise<PrepareResult> {
  const p = opts.onProgress ?? (() => {});

  p('reading');
  let doc: PDFDocument;
  try {
    doc = await PDFDocument.load(fileBytes, { ignoreEncryption: false });
  } catch (e) {
    const msg = String((e as Error)?.message ?? e);
    if (/encrypt/i.test(msg)) {
      throw new InteriorPrepareError(
        'PDF protégé par mot de passe — nous ne pouvons pas le lire. Ré-exportez-le sans protection.',
      );
    }
    throw new InteriorPrepareError(`PDF illisible ou corrompu. (${msg})`);
  }

  p('measuring');
  const pages = measurePages(doc);
  const plan = planConversion(pages, opts.bleedMm);
  const sourcePages = pages.length;

  if (plan.method === 'crop-a4') {
    p('converting', `${plan.source.wmm} × ${plan.source.hmm} mm → ${INTERIOR_TRIM_MM.wmm} × ${INTERIOR_TRIM_MM.hmm} mm`);
    cropToTrim(doc);
  }

  p('branding');
  let brandingAdded = 0;
  const introPage = await addBrandingPage(doc, opts.introPageUrl ?? null, 'start');
  if (introPage) brandingAdded++;
  if (await addBrandingPage(doc, opts.outroPageUrl ?? null, 'end')) brandingAdded++;

  // Bindery tag — the SAME order stamp as the generated path (lib/orderStamp.ts,
  // owner 2026-10-21), at the same place (5 mm from the spine edge, ~8 mm up):
  // pure black or white from the real pixels under it, boxed when busy, bold
  // 8 pt — so a manual interior and its cover carry a visibly identical label
  // and production can marry the two. On the INTRO page, not the customer's
  // first photo page; falls back to the first page when there is no intro, so
  // the tag is never lost. (The intro is inserted at index 0.)
  if (opts.orderLabel) {
    try {
      const helvBold = await doc.embedFont(StandardFonts.HelveticaBold);
      const target = introPage ?? doc.getPage(0);
      await drawOrderStamp({
        doc, page: target, pageIndex: doc.getPages().indexOf(target),
        text: opts.orderLabel,
        font: helvBold,
        place: (w, a, d) => placeStamp(5 * PT_PER_MM, 7 * PT_PER_MM, w, a, d, 'left'),
      });
    } catch (e) {
      console.warn('[interiorPrepare] order label failed:', (e as Error)?.message ?? e);
    }
  }

  p('padding');
  // LOCKED RULE (report #1074, mirrored from pdfGenerator.ts:2452-2483): pad to
  // a multiple of 4 by DUPLICATING THE OUTRO so the album ends on branded
  // pages. A white blank must never reach a printed album — the cream filler
  // below exists only for an order that has no outro page at all.
  const beforePad = doc.getPageCount();
  const target = padTo4(beforePad);
  let outroAdded = 0;
  let blanks = 0;
  for (let i = beforePad; i < target; i++) {
    if (opts.outroPageUrl && await addBrandingPage(doc, opts.outroPageUrl, 'end')) {
      outroAdded++;
      continue;
    }
    const blank = doc.addPage([INTERIOR_TRIM_MM.wmm * PT_PER_MM, INTERIOR_TRIM_MM.hmm * PT_PER_MM]);
    blank.drawRectangle({
      x: 0, y: 0,
      width: INTERIOR_TRIM_MM.wmm * PT_PER_MM,
      height: INTERIOR_TRIM_MM.hmm * PT_PER_MM,
      color: rgb(LETTERBOX_FILL.r, LETTERBOX_FILL.g, LETTERBOX_FILL.b),
    });
    blanks++;
  }

  const log: InteriorConversionLog = {
    at: new Date().toISOString(),
    filename: opts.filename,
    file_bytes: fileBytes.byteLength,
    source_mm: plan.source,
    target_mm: { ...INTERIOR_TRIM_MM },
    method: plan.method,
    source_pages: sourcePages,
    branding_pages_added: brandingAdded,
    outro_pages_added: outroAdded,
    blank_pages_added: blanks,
    final_pages: doc.getPageCount(),
    order_label: opts.orderLabel ?? null,
  };

  // Nothing to rewrite? Hand back the operator's original bytes untouched.
  // This is not just an optimisation: re-saving a 300 MB file costs another
  // full copy in memory, so skipping it is what keeps big already-correct
  // files working on modest machines.
  // ⚠️ THE PRINT GATE (2026-10-07): a client's own PDF is checked too — a
  // progressive JPEG inside it fails at the printer exactly like ours did
  // (« A drawing error occurred »). Refused here, with the page numbers.
  p('checking');
  await assertPrintSafe(doc, null, 'intérieur (PDF client)');
  if (plan.method === 'none' && brandingAdded === 0 && blanks === 0 && outroAdded === 0 && !opts.orderLabel) {
    return { bytes: fileBytes, log, unchanged: true };
  }

  p('saving');
  const out = await doc.save({ useObjectStreams: false });
  return { bytes: out, log, unchanged: false };
}
